import React from 'react';

import { Text } from '@rbilabs/universal-components';
import { useIntl } from 'react-intl';

import { useFormatCalories } from 'hooks/use-format-calories';
import { useUIContext } from 'state/ui';

import { ComposedCartItemType } from './hooks/use-compose-cart-item-ui-data';

interface CartItemPriceAndCaloriesProps {
  cartItem: ComposedCartItemType;
  isSingleItem: boolean;
}

// Temporarily suppressing calorie information on the frontend due to legal concerns.
// This will be reverted once the issue is resolved.
// See: https://rbidigital.slack.com/archives/C05DZTFFRQT/p1731961914027219 for more details.
const HIDE_CALORIES = true;

export const CartItemPriceAndCalories: React.FC<CartItemPriceAndCaloriesProps> = ({
  cartItem,
  isSingleItem,
}) => {
  const { modifiers, calories, price } = cartItem;
  const { formatMessage } = useIntl();
  const { formatPriceWithSign } = useUIContext();
  const formatCalories = useFormatCalories();

  // RegEx to cleanup multiple whitespaces and trim the string.
  const modifierName =
    modifiers
      ?.map(modifier => modifier.name?.trim())
      .filter(Boolean)
      .join(', ')
      ?.replace(/\s+/g, ' ')
      .trim() ?? '';

  const modifierPrice = modifiers?.reduce((acc, modifier) => acc + (modifier.price ?? 0), 0) ?? 0;
  const childPrice = price ?? 0;

  const cartItemCalories = calories ?? 0;
  const cartItemPrice = isSingleItem ? modifierPrice : childPrice + modifierPrice;

  const priceLabel = formatPriceWithSign(cartItemPrice);

  const caloriesLabel = HIDE_CALORIES ? '' : formatCalories(cartItemCalories);
  const priceAndCaloriesLabel =
    priceLabel && caloriesLabel
      ? `${priceLabel}${caloriesLabel ? ' | ' : ''}${caloriesLabel}`
      : undefined;

  if (!priceAndCaloriesLabel && !modifierName) {
    return null;
  }

  return (
    <>
      {priceAndCaloriesLabel && (
        <Text fontSize="xs">
          <Text>{priceAndCaloriesLabel}</Text>
        </Text>
      )}
      {modifierName && (
        <Text fontSize="xs" lineHeight="sm" flex={1}>
          <Text lineHeight="sm" bold>
            {formatMessage({ id: 'yourWay' }) + ' '}
          </Text>
          {modifierName}
        </Text>
      )}
    </>
  );
};
